/**
 * The attribution settings every channel-splitting page carries — Channels,
 * Product × Channel, and the customer-level splits on Refunds, Churn,
 * Cross-Sell and Demographics — so one rule reads the same everywhere.
 *
 * Each field renders only when its change handler is given: the customer-level
 * pages count whole customers, so they offer the engine but no order scope,
 * accounting date or credit window, and only the one-channel
 * models (CUSTOMER_MODELS; the backend echoes the authoritative list). There,
 * Last Non-Direct / Last Touch answer "who sold them" (a rep's customer is
 * Sales Team) and First Touch "who found them". See backend
 * services/attribution/userChannelMap.
 *
 * Lookback is not a setting: it is all time for every brand (see
 * lib/attributionLookback).
 */
import AttributionWindowControl from './AttributionWindowControl'

const controlClass = 'h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground'
const fieldClass = 'flex flex-col gap-1 text-xs text-muted-foreground'

export const DEFAULT_ENGINE = 'last-non-direct'

/** Until a response echoes its own list. Mirrors backend CUSTOMER_MODELS. */
export const CUSTOMER_MODELS = [
  { id: 'last-non-direct', label: 'Last Non-Direct Touch', description: 'Who sold them: the price-book closing touch (a rep is Sales Team), otherwise the latest non-direct visit.' },
  { id: 'last-touch', label: 'Last Touch', description: 'Who sold them: the price-book closing touch, or the latest visit.' },
  { id: 'first-touch', label: 'First Touch', description: 'Who found them: the earliest visit, even when a rep closed the sale.' },
]

/**
 * @param {object}   props
 * @param {object[]} props.models  [{id, label, description?}]
 * @param {string}   [props.engineGroupLabel]  wraps the models in an optgroup
 * @param {node}     [props.children]  extra engine <option>s after the models
 * @param {string}   [props.className]  the wrapper; 'contents' joins a parent row
 */
export default function AttributionSettings({
  models = CUSTOMER_MODELS, engineGroupLabel, children,
  engine, onEngineChange,
  scope, onScopeChange,
  accountingMode, onAccountingModeChange,
  attributionWindow, onAttributionWindowChange,
  className = 'flex flex-wrap items-end gap-3',
}) {
  const modelOptions = models.map(model => <option key={model.id} value={model.id} title={model.description}>{model.label}</option>)
  return <div className={className}>
    {onEngineChange && <label className={fieldClass}>Attribution engine
      <select className={controlClass} value={engine} onChange={event => onEngineChange(event.target.value)}>
        {engineGroupLabel ? <optgroup label={engineGroupLabel}>{modelOptions}</optgroup> : modelOptions}
        {children}
      </select>
    </label>}
    {onScopeChange && <label className={fieldClass}>Subscription filter
      <select className={controlClass} value={scope} onChange={event => onScopeChange(event.target.value)}>
        <option value="new">New orders</option>
        <option value="all">All paid orders</option>
      </select>
    </label>}
    {onAccountingModeChange && <label className={fieldClass}>Accounting mode
      <select className={controlClass} value={accountingMode} onChange={event => onAccountingModeChange(event.target.value)}>
        <option value="payment">Event date</option>
        <option value="touchpoint">Click date</option>
      </select>
    </label>}
    {onAttributionWindowChange && <AttributionWindowControl value={attributionWindow} onChange={onAttributionWindowChange} />}
  </div>
}
